const padDatePart = (value, length = 2) => String(value).padStart(length, '0');

export const getLocalDateKey = (date = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${padDatePart(date.getFullYear(), 4)}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

export const formatLocalTimestampWithOffset = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return new Date().toISOString();

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = padDatePart(Math.floor(absOffset / 60));
  const offsetMins = padDatePart(absOffset % 60);
  const datePart = getLocalDateKey(date);
  const timePart = `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}.${padDatePart(date.getMilliseconds(), 3)}`;

  return `${datePart}T${timePart}${sign}${offsetHours}:${offsetMins}`;
};

export const buildLocalOperationTimestamp = (dateYMD) => {
  const now = new Date();
  if (!dateYMD) return formatLocalTimestampWithOffset(now);

  const [year, month, day] = String(dateYMD).split('-').map((segment) => Number(segment));
  if (!year || !month || !day) return formatLocalTimestampWithOffset(now);

  return formatLocalTimestampWithOffset(
    new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()),
  );
};

// Día ('YYYY-MM-DD') + hora local ('HH:MM') → timestamp con offset local, para columnas
// timestamptz. Nunca mandar `${fecha}T${hora}:00` sin offset: Postgres lo toma como UTC.
export const buildLocalDateTimeTimestamp = (dateYMD, timeHM) => {
  if (!dateYMD || !timeHM) return null;

  const [year, month, day] = String(dateYMD).split('-').map((segment) => Number(segment));
  const [hours, minutes] = String(timeHM).split(':').map((segment) => Number(segment));
  if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  return formatLocalTimestampWithOffset(new Date(year, month - 1, day, hours, minutes, 0, 0));
};

export const buildLocalDateRangeTimestamp = (dateYMD, endOfDay = false) => {
  if (!dateYMD) return null;

  const [year, month, day] = String(dateYMD).split('-').map((segment) => Number(segment));
  if (!year || !month || !day) return null;

  const timeParts = endOfDay ? [23, 59, 59, 999] : [0, 0, 0, 0];
  return formatLocalTimestampWithOffset(new Date(year, month - 1, day, ...timeParts));
};

// ---------------------------------------------------------------------------
// Lectura y presentación
//
// Distinción central (ver docs/specs/p1-timestamps-locales.md):
//
//   - Columnas `timestamptz` (sales.created_at, movimientos_caja.fecha,
//     movimientos_financieros.fecha_operacion…) guardan un instante. Se muestran
//     con las funciones `formatOperation*`, que lo convierten a hora local.
//
//   - Columnas `date` puras (sales.fecha_venta, sales.fecha_facturacion,
//     gastos.fecha) guardan un día sin hora. Se muestran con
//     `formatDateOnlyValue`, que NO las convierte: hacerlo las correría un día
//     hacia atrás.
//
// Usar la función equivocada corre la fecha. La elección es por tipo de columna,
// no por pantalla.
// ---------------------------------------------------------------------------

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// Un 'YYYY-MM-DD' suelto lo parsea JavaScript como medianoche UTC, que en
// Argentina es el día anterior. Se arma como fecha local para que el día se
// respete aunque el valor llegue desde una columna `date`.
export const parseOperationInstant = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.match(DATE_ONLY_PATTERN);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getOperationDateKey = (value) => {
  const instant = parseOperationInstant(value);
  return instant ? getLocalDateKey(instant) : '';
};

export const formatOperationDate = (value) => {
  const instant = parseOperationInstant(value);
  if (!instant) return '';

  return instant.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatOperationTime = (value) => {
  const instant = parseOperationInstant(value);
  if (!instant) return '';

  return instant.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const formatOperationDateTime = (value) => {
  const instant = parseOperationInstant(value);
  if (!instant) return '';

  return `${formatOperationDate(instant)} ${formatOperationTime(instant)}`;
};

export const formatDateOnlyValue = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const raw = value instanceof Date ? getLocalDateKey(value) : String(value).trim();
  // Supabase devuelve las columnas `date` como 'YYYY-MM-DD'; si llegara un
  // timestamp completo, alcanza con quedarse con su parte de fecha.
  const dateOnly = raw.slice(0, 10).match(DATE_ONLY_PATTERN);
  if (!dateOnly) return '';

  const [, year, month, day] = dateOnly;
  return `${day}/${month}/${year}`;
};
